import React from 'react';
import { formatQuantity } from '../../../lib/utils/quantityFormat';

/**
 * Impegnato e disponibile, accanto alla giacenza.
 *
 * Sono due quantita' che l'utente annota a mano sulla scheda articolo: non
 * derivano da `stock_quantity` e non lo modificano. In particolare il
 * disponibile NON e' giacenza meno impegnato — chi li usa tiene fuori dal
 * disponibile anche quote non impegnate — quindi qui non si calcola niente,
 * si mostra quello che e' stato scritto.
 *
 * Non renderizza nulla se nessuno dei due e' valorizzato: chi non li usa non
 * si trova rumore in pagina. Se invece uno solo e' compilato, l'altro appare
 * come "—", perche' a quel punto il vuoto e' un'informazione (il campo esiste
 * ed e' stato lasciato in bianco) e non un dettaglio da nascondere.
 */

interface CommittedAvailableProps {
  committed?: number | null;
  available?: number | null;
  /**
   * 'inline'  accanto alla giacenza nelle liste del magazzino
   * 'block'   coppia di valori etichettati, per le schede di dettaglio
   * 'compact' scala tipografica del catalogo prodotti in offerta (10px),
   *           dove convivono con "Prodotto · Stock: N"
   */
  variant?: 'inline' | 'block' | 'compact';
  /** Mostra la coppia anche se nessuno dei due e' compilato (colonne fisse) */
  showEmpty?: boolean;
  /**
   * UDM dell'articolo (resolveProductUnit): decide il punto delle migliaia
   * (a pezzi non si mette). Non viene mostrata accanto ai valori.
   */
  unit?: string | null;
  className?: string;
}

export function CommittedAvailable({
  committed,
  available,
  variant = 'inline',
  showEmpty = false,
  unit,
  className = '',
}: CommittedAvailableProps) {
  const nessunoDeiDue = committed == null && available == null;
  if (nessunoDeiDue && !showEmpty) return null;

  const mostra = (v?: number | null) => formatQuantity(v, { unit, unitDisplay: 'none', fallback: '—' });

  if (variant === 'block') {
    return (
      <div className={`flex items-center gap-4 ${className}`}>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#787774] dark:text-gray-400">
            Impegnato
          </div>
          <div className="text-sm tabular-nums text-[#37352F] dark:text-white">
            {mostra(committed)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-wider text-[#787774] dark:text-gray-400">
            Disponibile
          </div>
          <div className="text-sm tabular-nums text-[#37352F] dark:text-white">
            {mostra(available)}
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'compact') {
    return (
      <>
        <span className="text-[10px] text-black/30 dark:text-white/30" aria-hidden="true">
          ·
        </span>
        <span className={`text-[10px] text-black/45 dark:text-white/50 tabular-nums ${className}`}>
          Imp: {mostra(committed)}
        </span>
        <span className="text-[10px] text-black/30 dark:text-white/30" aria-hidden="true">
          ·
        </span>
        <span className="text-[10px] text-black/45 dark:text-white/50 tabular-nums">
          Disp: {mostra(available)}
        </span>
      </>
    );
  }

  return (
    <span
      className={`inline-flex items-center gap-1.5 text-xs text-[#787774] dark:text-gray-400 tabular-nums ${className}`}
    >
      <span title="Impegnato">
        Imp. <span className="text-[#37352F] dark:text-gray-200">{mostra(committed)}</span>
      </span>
      <span aria-hidden="true">·</span>
      <span title="Disponibile">
        Disp. <span className="text-[#37352F] dark:text-gray-200">{mostra(available)}</span>
      </span>
    </span>
  );
}

export default CommittedAvailable;
